interface TextProcessingStrategy {
  processText(text: string, parentWidth: number, textOverflowEllipsis: boolean): string;
}

const measureWidth = (text: string, textStyles?: Record<string, string>): number => {
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.whiteSpace = 'nowrap';
  span.textContent = text;
  
  if (textStyles) {
    Object.entries(textStyles).forEach(([property, value]) => {
      // Using type assertion to handle the dynamic property assignment
      (span.style as any)[property] = value;
    });
  }
  
  document.body.appendChild(span);
  const width = span.offsetWidth;
  document.body.removeChild(span);
  return width;
};

export class SimpleTextProcessor implements TextProcessingStrategy {
  processText(text: string, parentWidth: number, textOverflowEllipsis: boolean, textStyles?: Record<string, string>): string {
    const tokens = text.split(/(\s+|\n)/);
    parentWidth = textOverflowEllipsis && parentWidth === 0 ? 1000 : parentWidth;
    const fullText = tokens.join('').trim();
    
    if (textOverflowEllipsis) {
      if (measureWidth(fullText, textStyles) > parentWidth) {
        let start = 0;
        let end = fullText.length;
        while (start < end) {
          const mid = Math.floor((start + end + 1) / 2);
          const testText = fullText.substring(0, mid) + '...';
          if (measureWidth(testText, textStyles) <= parentWidth) {
            start = mid;
          } else {
            end = mid - 1;
          }
        }
        return `<span class="shimmer-text">${fullText.substring(0, start)}...</span>`;
      }
      return `<span class="shimmer-text">${fullText}</span>`;
    }
    
    let result = '';
    let currentLine = '';
    tokens.forEach((token, i) => {
      if (textOverflowEllipsis && currentLine && measureWidth(currentLine + token, textStyles) > parentWidth) {
        result += `<span class="shimmer-text">${currentLine.trim()}</span>${i < tokens.length - 1 ? '<br/>' : ''}`;
        currentLine = token;
      } else if (token === '\n ' || token === '\n \n' || token === '\n \n ' || token === ' \n \n' || token === ' \n \n ') {
        result += `<span class="shimmer-text">${currentLine.trim()}</span><br/><br/>`;
        currentLine = '';
        return result;
      } else {
        currentLine = currentLine ? currentLine + token : token;
      }
    });
    
    if (currentLine) {
      result += `<span class="shimmer-text">${currentLine.trim()}</span>`;
    }
    
    return result;
  }
}

export class ComplexTextProcessor implements TextProcessingStrategy {
  processText(htmlString: string, parentWidth: number, textOverflowEllipsis: boolean, textStyles?: Record<string, string>): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
  
    function wrapTextNodes(node: Node) {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue && node.nodeValue.trim() !== '') {
        const textContent = node.nodeValue
          .split(/(\s+|\n)/)
          .flatMap(token => {
            // Skip if token is whitespace or already short
            if (/^\s*$/.test(token) || token.length < 10) return token;
            return token.split(/(-(?=\d{2}\.\d{2}\.\d{4})|(?<=\d{2}\.\d{2}\.\d{4})-|(?<=\w{3,})-(?=\w{3,}))/)
              .filter(part => part !== '');
          });

        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.whiteSpace = 'nowrap';

        if (textStyles) {
          Object.entries(textStyles).forEach(([property, value]) => {
            // Using type assertion to handle the dynamic property assignment
            (span.style as any)[property] = value;
          });
        }

        document.body.appendChild(span);
  
        let currentText = '';
        let wrappedText = '';
        const spans: string[] = [];

        textContent.forEach((token, index) => {
          if (token === '\n') {
            if (currentText.trim()) {
              spans.push(`<span class="shimmer-text">${currentText.trim()}</span>`);
            }
            spans.push('<br/>');
            currentText = '';
          } else if (token === '\n ' || token === '\n \n' || token === '\n \n ' || token === ' \n \n' || token === ' \n \n ') {
            if (currentText.trim()) {
              spans.push(`<span class="shimmer-text">${currentText.trim()}</span>`);
            }
            spans.push('<br/><br/>');
            currentText = '';
          } else {
            const testText = currentText ? `${currentText}${token}` : token;
            span.textContent = testText;
  
            if (span.offsetWidth > (parentWidth - 1) && currentText.trim()) {
              spans.push(`<span class="shimmer-text">${currentText.trim()}</span>`);
              spans.push('<br/>');
              currentText = token.trim();
            } else {
              currentText = testText;
            }
          }
  
          // Handle the last token
          if (index === textContent.length - 1 && currentText.trim()) {
            spans.push(`<span class="shimmer-text">${currentText.trim()}</span>`);
          }
        });
  
        wrappedText = spans.join('');
        document.body.removeChild(span);
        
        // Only replace the node if we have actual content to wrap
        if (wrappedText && spans.some(span => span.includes('shimmer-text'))) {
          const wrapperSpan = document.createElement('span');
          wrapperSpan.innerHTML = wrappedText;
          (node as ChildNode).replaceWith(wrapperSpan);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Convert NodeList to array to prevent issues with live updates
        Array.from(node.childNodes).forEach(wrapTextNodes);
      }
    }
  
    // Convert NodeList to array to prevent issues with live updates
    Array.from(doc.body.childNodes).forEach(node => wrapTextNodes(node));
    return doc.body.innerHTML;
  }
}